/**
 * Shared Recharts tooltip typography for statistics charts.
 * Title: primary font, tropical surf. Labels: primary font, gray-500.
 * P&L values: primary font, green/red (caller supplies color).
 */

import React from "react";

import { MetricDefinitionTooltip } from "@/components/ui/metric-tooltip";

export const STATS_CHART_TOOLTIP_TITLE_CLASS =
  "font-[family-name:var(--font-primary)] text-xs font-medium text-[color:var(--brand-tropical-surf)]";

export const STATS_CHART_TOOLTIP_LABEL_CLASS =
  "font-[family-name:var(--font-primary)] text-xs font-medium text-gray-500 dark:text-white";

export const STATS_CHART_TOOLTIP_VALUE_CLASS =
  "font-[family-name:var(--font-primary)] text-xs font-medium tabular-nums text-gray-500 dark:text-white";

export const STATS_CHART_TOOLTIP_PNL_VALUE_CLASS =
  "font-[family-name:var(--font-primary)] text-xs font-medium tabular-nums";

export const STATS_CHART_TOOLTIP_CONTENT_CLASS =
  "font-[family-name:var(--font-primary)] text-xs";

/** Empty chart state when the selected date range has no series points. */
export const STATISTICS_NO_DATA_TIMEFRAME_MESSAGE =
  "No data available for the selected timeframe.";

export const STATISTICS_NO_DATA_TIMEFRAME_CLASSNAME =
  "font-[family-name:var(--font-primary)] text-sm font-medium text-gray-500 dark:text-white";

/** Apply to ChartTooltipContent `className` for consistent statistics chart tooltips. */
export function statsChartTooltipContentClassName(widthClass = "w-[200px]"): string {
  return `${widthClass} ${STATS_CHART_TOOLTIP_CONTENT_CLASS}`;
}

/** @deprecated Use MetricDefinitionTooltip from @/components/ui/metric-tooltip */
export const StatisticsDefinitionTooltip = MetricDefinitionTooltip;

export function StatisticsChartTooltipRow({
  label,
  value,
  valueClassName = STATS_CHART_TOOLTIP_VALUE_CLASS,
  valueStyle,
}: {
  label: React.ReactNode;
  value: React.ReactNode;
  valueClassName?: string;
  valueStyle?: React.CSSProperties;
}) {
  return (
    <div className="flex w-full items-center justify-between gap-4">
      <span className={STATS_CHART_TOOLTIP_LABEL_CLASS}>{label}</span>
      <span className={valueClassName} style={valueStyle}>
        {value}
      </span>
    </div>
  );
}

export function StatisticsChartTooltipPnlRow({
  label,
  value,
  color,
  seriesColor,
  labelAdornment,
}: {
  label: React.ReactNode;
  value: React.ReactNode;
  color: string;
  /** Line/series color accent (e.g. simulation stroke) shown beside the row. */
  seriesColor?: string;
  labelAdornment?: React.ReactNode;
}) {
  return (
    <div className="flex w-full min-w-0 items-center gap-2">
      {seriesColor ? (
        <span
          className="h-3.5 w-0.5 shrink-0 rounded-[1px]"
          style={{ backgroundColor: seriesColor }}
          aria-hidden
        />
      ) : null}
      <div className="min-w-0 flex-1">
        <StatisticsChartTooltipRow
          label={
            labelAdornment ? (
              <span className="inline-flex items-center gap-1">
                <span>{label}</span>
                {labelAdornment}
              </span>
            ) : (
              label
            )
          }
          value={value}
          valueClassName={STATS_CHART_TOOLTIP_PNL_VALUE_CLASS}
          valueStyle={{ color }}
        />
      </div>
    </div>
  );
}

const STATS_CHART_TOOLTIP_SUBROW_LABEL_CLASS =
  "stats-chart-tooltip-subrow font-[family-name:var(--font-primary)] text-[11px] font-medium";

const STATS_CHART_TOOLTIP_SUBROW_VALUE_CLASS =
  "stats-chart-tooltip-subrow font-[family-name:var(--font-primary)] text-[11px] font-medium tabular-nums";

/** Nested transfer breakdown under the cumulative transfers tooltip row (not extra chart series). */
export function StatisticsChartTooltipTransferBreakdown({
  netLabel,
  netValue,
  breakdown,
  seriesColor,
  formatSignedCurrency,
}: {
  netLabel: React.ReactNode;
  netValue: React.ReactNode;
  breakdown?: {
    cumulative_deposits: number;
    cumulative_withdrawals: number;
    cumulative_financing: number;
    cumulative_commissions: number;
  };
  seriesColor?: string;
  formatSignedCurrency: (value: number, options?: { forceSign?: boolean }) => string;
}) {
  const subRows = breakdown
    ? [
        { label: "Deposits", value: breakdown.cumulative_deposits, forceSign: true },
        { label: "Withdrawals", value: breakdown.cumulative_withdrawals },
        { label: "Financing", value: breakdown.cumulative_financing },
        { label: "Commissions", value: breakdown.cumulative_commissions },
      ]
    : [];

  return (
    <div className="flex w-full min-w-0 flex-col gap-1">
      <div className="flex w-full min-w-0 items-center gap-2">
        {seriesColor ? (
          <span
            className="h-3.5 w-0.5 shrink-0 rounded-[1px]"
            style={{ backgroundColor: seriesColor }}
            aria-hidden
          />
        ) : null}
        <div className="min-w-0 flex-1">
          <StatisticsChartTooltipRow label={netLabel} value={netValue} />
        </div>
      </div>
      {subRows.length > 0 ? (
        <div className="ml-3 flex flex-col gap-0.5 border-l border-border/40 pl-2.5">
          {subRows.map((row) => (
            <div key={row.label} className="flex w-full items-center justify-between gap-3">
              <span
                className={STATS_CHART_TOOLTIP_SUBROW_LABEL_CLASS}
                data-statistics-chart-tooltip-subrow=""
              >
                {row.label}
              </span>
              <span
                className={STATS_CHART_TOOLTIP_SUBROW_VALUE_CLASS}
                data-statistics-chart-tooltip-subrow=""
              >
                {formatSignedCurrency(row.value, row.forceSign ? { forceSign: true } : undefined)}
              </span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
